/* ─────────────────────────────────────────────────────────────────────────
   assets/pdp-gallery.js
   PDP-GALLERY-1 · companion JS for snippets/pdp-gallery.liquid
   Implements HE_PDP_GALLERY_SPEC_v1 §4 interaction contract.

   - Click thumb → swap main image src/srcset/alt + caption + modifier class
   - Keyboard (←/→/↑/↓/Home/End) → cycle thumbs with automatic activation
   - Deep-link `#media=<key>` on initial load → activate matching thumb
   - Preload-before-swap (new Image()) so visible swap waits for bytes
   - Fades respect prefers-reduced-motion (CSS handles the 0ms case)

   No dependencies. Vanilla DOM API. Loaded via <script defer> from
   sections/product-archive-main.liquid.
   ───────────────────────────────────────────────────────────────────────── */
(function () {
  'use strict';

  /* ───────────────────────────────────────────────────────────────────
     initLightbox(root, image, caption)
     PDP-GALLERY-1 (lightbox pass) · simple accessible image lightbox.

     - Click main image trigger → open lightbox with currently active
       image src/srcset/alt + caption text (no new HTTP request — same
       URL as inline render, browser cache hit; `sizes="90vw"` hint lets
       browser pick the larger 1600w srcset entry if available).
     - Close on: close button click · Escape · click directly on the
       lightbox container (backdrop area outside content).
     - Scroll lock via `body.has-pdp-lightbox-open { overflow: hidden }`.
     - Focus management: save activeElement on open, focus close button,
       restore previous focus on close.
     - Minimal focus trap: Tab/Shift+Tab inside the lightbox always
       returns focus to the close button (only one focusable inside).
     - prefers-reduced-motion handled via CSS.

     No advanced pan/zoom · no magnifier · no drag-to-pan · no pinch-zoom
     custom handling · no in-modal carousel.
     ─────────────────────────────────────────────────────────────────── */
  function initLightbox(root, image, caption, options) {
    options = options || {};
    var lightbox = root.querySelector('[data-pdp-gallery-lightbox]');
    var trigger = root.querySelector('[data-pdp-gallery-lightbox-trigger]');
    if (!lightbox || !trigger) return;

    var lightboxImage = lightbox.querySelector('[data-pdp-gallery-lightbox-image]');
    var lightboxCaption = lightbox.querySelector('[data-pdp-gallery-lightbox-caption]');
    var lightboxClose = lightbox.querySelector('[data-pdp-gallery-lightbox-close]');
    var lightboxPrev = lightbox.querySelector('[data-pdp-gallery-lightbox-prev]');
    var lightboxNext = lightbox.querySelector('[data-pdp-gallery-lightbox-next]');
    if (!lightboxImage || !lightboxClose) return;

    // PDP-GALLERY-1 (carousel pass) · Optional nav contract — when the
    // host gallery is multi-image (v6 path with _show_nav), it passes
    // an `activateByOffset` function that advances the active thumb.
    // The lightbox calls it on prev/next; the active-thumb's data-*
    // contract is what the lightbox image syncs to via activate()'s
    // built-in lightbox-sync (see initGallery activate()).
    var activateByOffset = typeof options.activateByOffset === 'function' ? options.activateByOffset : null;
    var hasNav = !!activateByOffset && !!lightboxPrev && !!lightboxNext;

    var previousFocus = null;
    var escapeHandler = null;
    var tabTrapHandler = null;

    function openLightbox() {
      previousFocus = document.activeElement;

      // Sync lightbox with the CURRENT main image (after any thumb swap).
      var currentSrc = image.currentSrc || image.src;
      if (currentSrc) lightboxImage.setAttribute('src', currentSrc);
      var currentSrcset = image.getAttribute('srcset');
      if (currentSrcset) {
        lightboxImage.setAttribute('srcset', currentSrcset);
      } else {
        lightboxImage.removeAttribute('srcset');
      }
      // sizes="90vw" hints to the browser to pick the larger srcset entry
      // (typically 1600w) for the bigger render. Same URL the inline main
      // image used, so most of the time it's a cache hit.
      lightboxImage.setAttribute('sizes', '90vw');
      lightboxImage.setAttribute('alt', image.getAttribute('alt') || '');

      if (lightboxCaption && caption) {
        lightboxCaption.textContent = caption.textContent || '';
      } else if (lightboxCaption) {
        lightboxCaption.textContent = '';
      }

      lightbox.removeAttribute('hidden');
      lightbox.setAttribute('aria-hidden', 'false');
      document.body.classList.add('has-pdp-lightbox-open');

      // PDP-GALLERY-1 (carousel pass) · Document-level keydown:
      //   Escape          → close
      //   ArrowRight      → next image (if nav)
      //   ArrowLeft       → previous image (if nav)
      escapeHandler = function (e) {
        if (e.key === 'Escape' || e.key === 'Esc') {
          e.preventDefault();
          closeLightbox();
          return;
        }
        if (hasNav) {
          if (e.key === 'ArrowRight') {
            e.preventDefault();
            activateByOffset(1);
          } else if (e.key === 'ArrowLeft') {
            e.preventDefault();
            activateByOffset(-1);
          }
        }
      };
      document.addEventListener('keydown', escapeHandler);

      // PDP-GALLERY-1 (carousel pass) · Focus trap — cycle through all
      // focusable elements inside the dialog. With nav: [prev, close,
      // next]. Without nav: [close] only (loops back to itself).
      tabTrapHandler = function (e) {
        if (e.key !== 'Tab') return;
        var focusables = [];
        if (lightboxPrev && !lightboxPrev.hasAttribute('hidden') && !lightboxPrev.disabled) focusables.push(lightboxPrev);
        if (lightboxClose) focusables.push(lightboxClose);
        if (lightboxNext && !lightboxNext.hasAttribute('hidden') && !lightboxNext.disabled) focusables.push(lightboxNext);
        if (focusables.length === 0) return;
        var currentIndex = focusables.indexOf(document.activeElement);
        var nextIndex;
        if (e.shiftKey) {
          nextIndex = currentIndex <= 0 ? focusables.length - 1 : currentIndex - 1;
        } else {
          nextIndex = currentIndex === -1 || currentIndex === focusables.length - 1 ? 0 : currentIndex + 1;
        }
        e.preventDefault();
        focusables[nextIndex].focus();
      };
      lightbox.addEventListener('keydown', tabTrapHandler);

      requestAnimationFrame(function () {
        if (typeof lightboxClose.focus === 'function') lightboxClose.focus();
      });
    }

    function closeLightbox() {
      lightbox.setAttribute('hidden', '');
      lightbox.setAttribute('aria-hidden', 'true');
      document.body.classList.remove('has-pdp-lightbox-open');

      if (escapeHandler) {
        document.removeEventListener('keydown', escapeHandler);
        escapeHandler = null;
      }
      if (tabTrapHandler) {
        lightbox.removeEventListener('keydown', tabTrapHandler);
        tabTrapHandler = null;
      }

      if (previousFocus && typeof previousFocus.focus === 'function') {
        previousFocus.focus();
      }
      previousFocus = null;
    }

    // PDP-GALLERY-FIX-2026-06-07 (v2) · Bind click DIRECTLY on every
    // [data-pdp-gallery-lightbox-trigger] that exists at init time.
    // Also keep a delegation handler for triggers added later (v6 swap
    // path) or for cases where the direct binding misses an element
    // because it was hidden at init time (fallback path).
    function bindTrigger(t) {
      if (t.__lbBound) return;
      t.__lbBound = true;
      t.addEventListener('click', function (e) {
        e.preventDefault();
        var img = t.querySelector('.pdp-gallery__media-img') ||
                  t.querySelector('img') || image;
        image = img;
        openLightbox();
      });
    }
    var allTriggers = root.querySelectorAll('[data-pdp-gallery-lightbox-trigger]');
    Array.prototype.forEach.call(allTriggers, bindTrigger);
    root.addEventListener('click', function (e) {
      var clickedTrigger = e.target.closest('[data-pdp-gallery-lightbox-trigger]');
      if (!clickedTrigger) return;
      bindTrigger(clickedTrigger); // idempotent — won't re-bind if already bound
      e.preventDefault();
      var img = clickedTrigger.querySelector('.pdp-gallery__media-img') ||
                clickedTrigger.querySelector('img') || image;
      image = img;
      openLightbox();
    });

    lightboxClose.addEventListener('click', function (e) {
      e.preventDefault();
      closeLightbox();
    });

    // PDP-GALLERY-1 (carousel pass) · Lightbox prev/next click. Delegates
    // to activateByOffset which updates the active thumb + main image +
    // counter; activate() then syncs the lightbox image because the
    // lightbox is currently open (aria-hidden="false").
    if (hasNav) {
      lightboxPrev.addEventListener('click', function (e) {
        e.preventDefault();
        activateByOffset(-1);
      });
      lightboxNext.addEventListener('click', function (e) {
        e.preventDefault();
        activateByOffset(1);
      });
    }

    // Click directly on the lightbox container (outside the content) closes.
    lightbox.addEventListener('click', function (e) {
      if (e.target === lightbox) closeLightbox();
    });
  }

  function initGallery(root) {
    var main = root.querySelector('[data-pdp-gallery-main]');
    var image = root.querySelector('.pdp-gallery__media-img');
    var caption = root.querySelector('[data-pdp-gallery-caption]');
    var thumbList = root.querySelector('[data-pdp-gallery-thumbs]');
    var thumbs = thumbList
      ? Array.prototype.slice.call(thumbList.querySelectorAll('[data-pdp-gallery-thumb]'))
      : [];

    if (!main || !image) return;
    if (root.hasAttribute('data-pdp-gallery-bound')) return;
    root.setAttribute('data-pdp-gallery-bound', 'true');

    var isFallback = main.hasAttribute('data-pdp-gallery-fallback');

    // ── Fallback path: lightbox only, no nav ──────────────────────────
    // product.media path uses different markup that this controller
    // doesn't manage. We still wire the click-to-enlarge affordance for
    // the single visible main image, but no inter-image navigation —
    // the lightbox markup itself was rendered without nav arrows /
    // counter (gated on _show_nav in the snippet, which only triggers
    // for v6 path).
    if (isFallback || thumbs.length === 0) {
      // 2026-10-19 · GALLERY ZOOM · a multi-image product.media gallery has its
      // own lightbox (bootFallbackThumbs). Binding this one too made every tap
      // load slide 1 first, and on close it focused slide 1's button, which
      // scrolled the mobile track back to slide 1.
      if (isFallback && root.querySelector('[data-pdp-gallery-fallback-thumbs] [data-pdp-gallery-thumb]')) return;
      initLightbox(root, image, caption);
      return;
    }

    // PDP-GALLERY-1 (carousel pass) · Counter + lightbox sync elements.
    // Resolved once per init; activate() updates them when set.
    var counterCurrent = root.querySelector('[data-pdp-gallery-counter-current]');
    var lightboxCounterCurrent = root.querySelector('[data-pdp-gallery-lightbox-counter-current]');
    var lightboxRoot = root.querySelector('[data-pdp-gallery-lightbox]');
    var lightboxImage = root.querySelector('[data-pdp-gallery-lightbox-image]');
    var lightboxCaption = root.querySelector('[data-pdp-gallery-lightbox-caption]');

    function activate(thumb, options) {
      options = options || {};
      var focusThumb = !!options.focusThumb;

      var newKey = thumb.getAttribute('data-media-key');
      var newModifier = thumb.getAttribute('data-modifier');
      var newSrc = thumb.getAttribute('data-src-1200') || '';
      var newSrcset = thumb.getAttribute('data-srcset') || '';
      var newCaption = thumb.getAttribute('data-caption') || '';
      var newAlt = thumb.getAttribute('data-alt') || '';

      // Update aria-selected / tabindex / is-active across all thumbs.
      thumbs.forEach(function (t) {
        var isActive = t === thumb;
        t.classList.toggle('is-active', isActive);
        t.setAttribute('aria-selected', isActive ? 'true' : 'false');
        t.setAttribute('tabindex', isActive ? '0' : '-1');
      });

      // PDP-GALLERY-1 (carousel pass) · Update 1-based counter immediately
      // (before fade) so the position indicator reads in sync with the
      // active thumb state. aria-live="polite" announces the change.
      var oneBasedIndex = thumbs.indexOf(thumb) + 1;
      if (oneBasedIndex > 0) {
        if (counterCurrent) counterCurrent.textContent = String(oneBasedIndex);
        if (lightboxCounterCurrent) lightboxCounterCurrent.textContent = String(oneBasedIndex);
      }

      // Preload the new image so the fade-in doesn't show a flash of nothing.
      var preloader = new Image();
      preloader.onload = doSwap;
      preloader.onerror = doSwap;
      preloader.src = newSrc;

      // Fade out immediately (CSS handles duration / reduced-motion).
      image.classList.add('is-swapping');
      if (caption) caption.classList.add('is-swapping');

      function doSwap() {
        // Wait one transition tick (~120ms) before swapping content so the
        // fade-out completes visually before fade-in begins. Under
        // prefers-reduced-motion the CSS transition is 0ms — visual swap is
        // still instant; the setTimeout adds a small no-op delay.
        setTimeout(function () {
          // Update modifier class on the main container.
          var className = main.className.replace(/\bpdp-gallery__main--\S+/g, '').trim();
          if (newModifier) className += ' pdp-gallery__main--' + newModifier;
          main.className = className;

          // Swap image attributes.
          if (newSrc) image.setAttribute('src', newSrc);
          if (newSrcset) {
            image.setAttribute('srcset', newSrcset);
          } else {
            image.removeAttribute('srcset');
          }
          if (newAlt) image.setAttribute('alt', newAlt);

          // Swap caption (single caption element shared across thumbs).
          if (caption) {
            caption.textContent = newCaption;
            if (newKey) caption.setAttribute('data-media-key', newKey);
          }

          // PDP-GALLERY-1 (carousel pass) · If the lightbox is currently
          // open, keep its image + caption in sync with the new active
          // thumb so prev/next inside the lightbox reflects immediately.
          if (lightboxRoot && lightboxRoot.getAttribute('aria-hidden') === 'false') {
            if (lightboxImage) {
              if (newSrc) lightboxImage.setAttribute('src', newSrc);
              if (newSrcset) {
                lightboxImage.setAttribute('srcset', newSrcset);
              } else {
                lightboxImage.removeAttribute('srcset');
              }
              lightboxImage.setAttribute('alt', newAlt || '');
              lightboxImage.setAttribute('sizes', '90vw');
            }
            if (lightboxCaption) lightboxCaption.textContent = newCaption;
          }

          // Fade back in next frame.
          requestAnimationFrame(function () {
            image.classList.remove('is-swapping');
            if (caption) caption.classList.remove('is-swapping');
          });
        }, 120);
      }

      // Scroll thumb into view on overflow (smooth on supporting browsers).
      if (typeof thumb.scrollIntoView === 'function') {
        try {
          thumb.scrollIntoView({ block: 'nearest', inline: 'nearest', behavior: 'smooth' });
        } catch (_e) {
          thumb.scrollIntoView();
        }
      }

      if (focusThumb && typeof thumb.focus === 'function') thumb.focus();
    }

    // PDP-GALLERY-1 (carousel pass) · Helper for prev/next arrow handlers.
    // Cyclic — matches the existing thumb keyboard pattern in Pass 1.
    function activateByOffset(offset) {
      var currentIndex = thumbs.findIndex(function (t) {
        return t.classList.contains('is-active');
      });
      if (currentIndex < 0) currentIndex = 0;
      var nextIndex = (currentIndex + offset + thumbs.length) % thumbs.length;
      activate(thumbs[nextIndex], { focusThumb: false });
    }

    // Click handler — direct activation, no focus shift (preserves user
    // focus context if they came from elsewhere).
    thumbs.forEach(function (thumb) {
      thumb.addEventListener('click', function (e) {
        e.preventDefault();
        activate(thumb, { focusThumb: false });
      });
    });

    // Keyboard handler — automatic activation pattern per WAI-ARIA tabs.
    thumbList.addEventListener('keydown', function (e) {
      var currentIndex = thumbs.findIndex(function (t) {
        return t.classList.contains('is-active');
      });
      if (currentIndex < 0) currentIndex = 0;

      var nextIndex = -1;
      switch (e.key) {
        case 'ArrowRight':
        case 'ArrowDown':
          nextIndex = (currentIndex + 1) % thumbs.length;
          break;
        case 'ArrowLeft':
        case 'ArrowUp':
          nextIndex = (currentIndex - 1 + thumbs.length) % thumbs.length;
          break;
        case 'Home':
          nextIndex = 0;
          break;
        case 'End':
          nextIndex = thumbs.length - 1;
          break;
        case 'Enter':
        case ' ': // Space
          // Redundant with the browser's default button-activates-on-Space
          // behavior, but explicit handling avoids any focus drift.
          if (document.activeElement && document.activeElement.matches('[data-pdp-gallery-thumb]')) {
            e.preventDefault();
            activate(document.activeElement, { focusThumb: false });
          }
          return;
        default:
          return;
      }

      e.preventDefault();
      activate(thumbs[nextIndex], { focusThumb: true });
    });

    // ── Main image prev/next arrows ────────────────────────────────────
    // PDP-GALLERY-1 (carousel pass) · Cyclic prev/next. Markup renders
    // only when total > 1 (gated in Liquid by _show_nav). querySelector
    // returns null when not rendered — guarded by truthy check.
    var mainPrev = root.querySelector('[data-pdp-gallery-main-prev]');
    var mainNext = root.querySelector('[data-pdp-gallery-main-next]');
    if (mainPrev) {
      mainPrev.addEventListener('click', function (e) {
        e.preventDefault();
        activateByOffset(-1);
      });
    }
    if (mainNext) {
      mainNext.addEventListener('click', function (e) {
        e.preventDefault();
        activateByOffset(1);
      });
    }

    // ── Thumb-strip overflow arrows ────────────────────────────────────
    // PDP-GALLERY-1 (carousel pass) · When the thumb strip overflows its
    // container, JS shows prev/next arrows that scroll the strip
    // horizontally (visual only — does NOT change selection). Visibility
    // is bound to the live scrollLeft / scrollWidth state via a small
    // helper invoked on init + on scroll + on resize.
    var thumbsPrev = root.querySelector('[data-pdp-gallery-thumbs-prev]');
    var thumbsNext = root.querySelector('[data-pdp-gallery-thumbs-next]');

    function updateThumbArrowVisibility() {
      if (!thumbList) return;
      var sw = thumbList.scrollWidth;
      var cw = thumbList.clientWidth;
      var sl = thumbList.scrollLeft;
      var canScrollLeft = sl > 1;
      var canScrollRight = sl < (sw - cw - 1);
      var hasOverflow = sw > cw + 1;
      if (thumbsPrev) {
        if (hasOverflow && canScrollLeft) {
          thumbsPrev.removeAttribute('hidden');
        } else {
          thumbsPrev.setAttribute('hidden', '');
        }
      }
      if (thumbsNext) {
        if (hasOverflow && canScrollRight) {
          thumbsNext.removeAttribute('hidden');
        } else {
          thumbsNext.setAttribute('hidden', '');
        }
      }
    }

    function scrollThumbsBy(direction) {
      if (!thumbList || thumbs.length === 0) return;
      // Scroll by ~2 thumb widths per click for a noticeable shift.
      var step = (thumbs[0].offsetWidth || 80) + 8;
      var delta = direction * step * 2;
      if (typeof thumbList.scrollBy === 'function') {
        thumbList.scrollBy({ left: delta, behavior: 'smooth' });
      } else {
        thumbList.scrollLeft += delta;
      }
    }

    if (thumbsPrev) {
      thumbsPrev.addEventListener('click', function (e) {
        e.preventDefault();
        scrollThumbsBy(-1);
      });
    }
    if (thumbsNext) {
      thumbsNext.addEventListener('click', function (e) {
        e.preventDefault();
        scrollThumbsBy(1);
      });
    }
    thumbList.addEventListener('scroll', updateThumbArrowVisibility);
    window.addEventListener('resize', updateThumbArrowVisibility);
    // Initial visibility check after layout settles.
    requestAnimationFrame(updateThumbArrowVisibility);

    // ── Lightbox with nav (v6 path) ────────────────────────────────────
    initLightbox(root, image, caption, { thumbs: thumbs, activateByOffset: activateByOffset });

    // Deep-link via URL hash: /products/foo#media=contour
    var hashMatch = (window.location.hash || '').match(/^#media=([a-z0-9-]+)$/i);
    if (hashMatch) {
      var targetKey = hashMatch[1].toLowerCase();
      var targetThumb = thumbs.find(function (t) {
        return (t.getAttribute('data-media-key') || '').toLowerCase() === targetKey;
      });
      if (targetThumb && !targetThumb.classList.contains('is-active')) {
        activate(targetThumb, { focusThumb: false });
      }
    }
  }

  function bootAll() {
    var galleries = document.querySelectorAll('.pdp-gallery');
    Array.prototype.forEach.call(galleries, initGallery);
    bootThumbStripArrows();
    bootFallbackThumbs();
  }

  /* ─────────────────────────────────────────────────────────────────────
     STEP 56 · Thumb strip arrows
     Works on ANY .pdp-gallery__thumbs-wrap on the page (v6-slot path and
     the fallback path — both now emit the same markup). Show/hide arrows
     based on overflow + scroll position. Click → scroll the strip by ~70%
     of its visible width. Idempotent (skip if already wired).
     ───────────────────────────────────────────────────────────────────── */
  function bootThumbStripArrows() {
    var wraps = document.querySelectorAll('.pdp-gallery__thumbs-wrap');
    Array.prototype.forEach.call(wraps, function (wrap) {
      if (wrap.__s56ArrowsInit) return;
      wrap.__s56ArrowsInit = true;
      var strip = wrap.querySelector('.pdp-gallery__thumbs');
      var prevBtn = wrap.querySelector('.pdp-gallery__thumbs-arrow--prev');
      var nextBtn = wrap.querySelector('.pdp-gallery__thumbs-arrow--next');
      if (!strip || !prevBtn || !nextBtn) return;

      function update() {
        var max = strip.scrollWidth - strip.clientWidth;
        var pos = Math.round(strip.scrollLeft);
        var overflowing = max > 4;
        if (!overflowing) {
          prevBtn.hidden = true;
          nextBtn.hidden = true;
          return;
        }
        prevBtn.hidden = pos <= 2;
        nextBtn.hidden = pos >= max - 2;
      }
      function step(dir) {
        var delta = Math.max(120, Math.floor(strip.clientWidth * 0.7));
        strip.scrollBy({ left: dir * delta, behavior: 'smooth' });
      }

      prevBtn.addEventListener('click', function (e) { e.preventDefault(); step(-1); });
      nextBtn.addEventListener('click', function (e) { e.preventDefault(); step(1); });
      strip.addEventListener('scroll', update, { passive: true });
      window.addEventListener('resize', update, { passive: true });
      update();
    });
  }

  /* ─────────────────────────────────────────────────────────────────────
     STEP 56 · Fallback gallery thumb-click swap + counter
     The fallback gallery (product.media path) used to early-return without
     thumb navigation. Now its thumbs are inside .pdp-gallery__thumbs-wrap
     (with arrows) and a counter renders next to it. This handler wires
     thumb-click → swap which figure is shown + update counter. Scoped to
     [data-pdp-gallery-fallback] so it does NOT touch the v6-slot path
     (which has its own logic above).
     ───────────────────────────────────────────────────────────────────── */
  // PDP-GALLERY-FIX-2026-06-08 · Self-contained fallback lightbox to avoid
  // conflicts with initLightbox handlers. Each fallback gallery owns its
  // own open/close/nav logic. e.stopPropagation() on the main-click handler
  // prevents initLightbox's delegation from firing too.
  function bootFallbackThumbs() {
    var fallbackMains = document.querySelectorAll('[data-pdp-gallery-fallback]');
    Array.prototype.forEach.call(fallbackMains, function (main) {
      if (main.__s56FallbackInit) return;
      main.__s56FallbackInit = true;
      var root = main.closest('.pdp-gallery') || document;
      var figures = Array.prototype.slice.call(main.querySelectorAll('.pdp-gallery__media'));
      var thumbs = Array.prototype.slice.call(root.querySelectorAll('[data-pdp-gallery-fallback-thumbs] [data-pdp-gallery-thumb]'));
      var counterCurrent = root.querySelector('[data-pdp-gallery-counter-current]');
      if (figures.length === 0 || thumbs.length === 0) return;

      // ── Lightbox elements ──────────────────────────────────────────
      var lb = root.querySelector('[data-pdp-gallery-lightbox]');
      var lbImg = lb ? lb.querySelector('[data-pdp-gallery-lightbox-image]') : null;
      var lbCaption = lb ? lb.querySelector('[data-pdp-gallery-lightbox-caption]') : null;
      var lbClose = lb ? lb.querySelector('[data-pdp-gallery-lightbox-close]') : null;
      var lbPrev = lb ? lb.querySelector('[data-pdp-gallery-lightbox-prev]') : null;
      var lbNext = lb ? lb.querySelector('[data-pdp-gallery-lightbox-next]') : null;
      var lbCounterCurrent = lb ? lb.querySelector('[data-pdp-gallery-lightbox-counter-current]') : null;
      var lbIsOpen = false;
      var escHandler = null;
      var lbReturnFocus = null;

      // 2026-10-19 · GALLERY ZOOM · the lightbox shows the image the PDP already
      // loaded (no blank frame), then swaps to a 2400 px source fetched only now
      // that the lightbox is open. Shopify returns the original if it is smaller.
      function isZoomable(fig) {
        return !!(fig && fig.querySelector('.pdp-gallery__media-img') && !fig.querySelector('video'));
      }
      function largeSrc(src) {
        if (!src || !/\/cdn\/shop\//.test(src)) return src;
        try {
          var u = new URL(src, window.location.href);
          u.searchParams.set('width', '2400');
          return u.toString();
        } catch (_e) { return src; }
      }
      function setLightboxImage(fig) {
        var img = fig.querySelector('.pdp-gallery__media-img');
        var shown = img.currentSrc || img.src;
        var big = largeSrc(img.getAttribute('src') || shown);
        lbImg.removeAttribute('srcset');
        lbImg.removeAttribute('sizes');
        lbImg.setAttribute('src', shown);
        lbImg.setAttribute('alt', img.getAttribute('alt') || '');
        lbImg.setAttribute('data-large-src', big);
        if (big && big !== shown) {
          var pre = new Image();
          pre.onload = function () {
            if (lbImg.getAttribute('data-large-src') === big) lbImg.setAttribute('src', big);
          };
          pre.src = big;
        }
      }

      function openFbLightbox(fig) {
        if (!lb || !lbImg) return;
        // VIDEO-1 · video_tag emits a small fallback <img> INSIDE <video>.
        // The lightbox must never pick that up.
        // VIDEO-2 · the poster attribute is Shopify's _small variant (92x123).
        // Prefer data-lightbox-src, emitted at width 1600 by the snippet.
        // 2026-10-19 · GALLERY ZOOM · only image slides open (video and the scale
        // slide never do).
        if (!isZoomable(fig)) return;
        lbReturnFocus = document.activeElement;
        setLightboxImage(fig);
        if (lbCaption) lbCaption.textContent = '';
        lb.removeAttribute('hidden');
        lb.setAttribute('aria-hidden', 'false');
        document.body.classList.add('has-pdp-lightbox-open');
        lbIsOpen = true;
        if (lbCounterCurrent) {
          var idx = figures.indexOf(fig);
          if (idx >= 0) lbCounterCurrent.textContent = String(idx + 1);
        }
        // PDP-MOBILE-10 · instrument the lightbox. Until now there was no way
        // to tell whether visitors open the gallery or only look at the images
        // in place. Fired through the theme's own emitter, guarded so a
        // blocked or absent analytics bundle can never break the lightbox.
        try {
          if (window.HE && typeof window.HE.track === 'function') {
            var _slide = figures.indexOf(fig);
            window.HE.track('gallery_lightbox_open', {
              slide_index: _slide >= 0 ? _slide + 1 : null,
              slide_count: figures.length,
              media_type: (fig && fig.querySelector('video')) ? 'video' : 'image',
              viewport: (window.matchMedia && window.matchMedia('(max-width: 991px)').matches) ? 'mobile' : 'desktop'
            });
          }
        } catch (_eTrack) {}
        if (escHandler) document.removeEventListener('keydown', escHandler);
        escHandler = function(e) {
          if (e.key === 'Escape' || e.key === 'Esc') closeFbLightbox();
          else if (e.key === 'ArrowLeft') { e.preventDefault(); nav(-1); }
          else if (e.key === 'ArrowRight') { e.preventDefault(); nav(1); }
          else if (e.key === 'Tab') { // keep focus inside the dialog
            var f = Array.prototype.filter.call(lb.querySelectorAll('button, [href], [tabindex]:not([tabindex="-1"])'), function (el) { return el.offsetParent !== null; });
            if (!f.length) return;
            var i0 = f.indexOf(document.activeElement);
            if (e.shiftKey && i0 <= 0) { e.preventDefault(); f[f.length - 1].focus(); }
            else if (!e.shiftKey && (i0 === -1 || i0 === f.length - 1)) { e.preventDefault(); f[0].focus(); }
          }
        };
        document.addEventListener('keydown', escHandler);
        if (lbClose) { try { lbClose.focus({ preventScroll: true }); } catch (_eF) { lbClose.focus(); } }
      }

      function closeFbLightbox() {
        if (!lb) return;
        lb.setAttribute('hidden', '');
        lb.setAttribute('aria-hidden', 'true');
        document.body.classList.remove('has-pdp-lightbox-open');
        lbIsOpen = false;
        if (escHandler) { document.removeEventListener('keydown', escHandler); escHandler = null; }
        // 2026-10-19 · GALLERY ZOOM · on mobile the slide on screen is the track's
        // scroll position, not .is-active: bring the track to the slide the
        // lightbox ended on, so the counter and dots match it.
        var cur = null;
        for (var i = 0; i < figures.length; i++) { if (figures[i].classList.contains('is-active')) { cur = figures[i]; break; } }
        if (cur && main.scrollWidth > main.clientWidth + 1) {
          var left = main.scrollLeft + cur.getBoundingClientRect().left - main.getBoundingClientRect().left;
          main.scrollLeft = left;
        }
        var back = cur && cur.querySelector('[data-pdp-gallery-lightbox-trigger]');
        var target = back || lbReturnFocus;
        if (target && typeof target.focus === 'function') { try { target.focus({ preventScroll: true }); } catch (_eF) {} }
        lbReturnFocus = null;
      }

      if (lbClose) lbClose.addEventListener('click', function(e) { e.preventDefault(); closeFbLightbox(); });
      if (lb) lb.addEventListener('click', function(e) { if (e.target === lb) closeFbLightbox(); });

      // ── show(index) ────────────────────────────────────────────────
      function show(index) {
        figures.forEach(function (fig, i) {
          var active = i === index;
          fig.classList.toggle('is-active', active);
          if (active) fig.removeAttribute('hidden'); else fig.setAttribute('hidden', '');
          // VIDEO-1 · the clip starts when its slide becomes active and stops
          // when it leaves. Muted, so play() needs no user gesture.
          var vid = fig.querySelector('video');
          if (vid) {
            if (active) {
              vid.muted = true;
              var pr = vid.play();
              if (pr && typeof pr.catch === 'function') pr.catch(function () {});
            } else {
              vid.pause();
              try { vid.currentTime = 0; } catch (_e) {}
            }
          }
        });
        thumbs.forEach(function (t, i) {
          t.classList.toggle('is-active', i === index);
          t.setAttribute('aria-current', i === index ? 'true' : 'false');
        });
        if (counterCurrent) counterCurrent.textContent = String(index + 1);
        // Sync lightbox if open
        if (lbIsOpen && lb) {
          var activeFig = figures[index];
          if (activeFig) {
            if (isZoomable(activeFig) && lbImg) setLightboxImage(activeFig);
            if (lbCounterCurrent) lbCounterCurrent.textContent = String(index + 1);
          }
        }
      }

      // ── VIDEO-3 · single centred play/pause toggle ─────────────────
      // The <video> is rendered with controls: false, so this button is the
      // only control on the slide — and, since the clip autoplays and loops,
      // it is also the WCAG 2.2.2 pause mechanism. It never auto-hides.
      // The click handler only calls play()/pause(); the button's glyph and
      // aria-label are driven by the element's own `play`/`pause` events, so
      // they stay correct when playback changes for any other reason (slide
      // change, tab backgrounding, the browser stopping it).
      figures.forEach(function (fig) {
        var toggle = fig.querySelector('[data-pdp-gallery-video-toggle]');
        var vid = fig.querySelector('video');
        if (!toggle || !vid) return;
        if (toggle.__s56VideoToggleInit) return;
        toggle.__s56VideoToggleInit = true;

        function render(playing) {
          toggle.setAttribute('data-playing', playing ? 'true' : 'false');
          toggle.setAttribute('aria-label', playing ? 'Pause video' : 'Play video');
        }

        toggle.addEventListener('click', function (e) {
          e.preventDefault();
          e.stopPropagation(); // don't let the lightbox delegation on `main` fire
          if (vid.paused) {
            vid.muted = true;
            var pr = vid.play();
            if (pr && typeof pr.catch === 'function') pr.catch(function () {});
          } else {
            vid.pause();
          }
        });

        vid.addEventListener('play', function () { render(true); });
        vid.addEventListener('pause', function () { render(false); });
        render(!vid.paused);
      });

      // ── Thumb clicks ───────────────────────────────────────────────
      thumbs.forEach(function (thumb, i) {
        thumb.addEventListener('click', function (e) {
          e.preventDefault();
          show(i);
        });
      });

      // ── Main image click → open lightbox (delegation on `main`) ──
      main.addEventListener('click', function(e) {
        if (!e.target.closest('[data-pdp-gallery-lightbox-trigger]')) return;
        e.preventDefault();
        e.stopPropagation(); // prevent initLightbox delegation from also firing
        // 2026-10-19 · GALLERY ZOOM · open the slide that was tapped. On mobile the
        // visible slide is the swipe track's scroll position and .is-active stays
        // on slide 1, which is why every tap used to open slide 1.
        var tapped = e.target.closest('.pdp-gallery__media');
        var activeFig = (tapped && figures.indexOf(tapped) >= 0) ? tapped : null;
        if (!activeFig) {
          for (var i = 0; i < figures.length; i++) {
            if (figures[i].classList.contains('is-active') && !figures[i].hasAttribute('hidden')) {
              activeFig = figures[i]; break;
            }
          }
        }
        activeFig = activeFig || figures[0];
        if (!isZoomable(activeFig)) return;
        var idx = figures.indexOf(activeFig);
        if (idx >= 0 && !activeFig.classList.contains('is-active')) show(idx);
        openFbLightbox(activeFig);
      });

      // ── Lightbox prev/next ─────────────────────────────────────────
      function nav(delta) {
        var current = -1;
        for (var i = 0; i < figures.length; i++) {
          if (figures[i].classList.contains('is-active')) { current = i; break; }
        }
        if (current < 0) current = 0;
        var next = current;
        // 2026-10-19 · GALLERY ZOOM · step over slides that are not images.
        for (var n = 0; n < figures.length; n++) {
          next = (next + delta + figures.length) % figures.length;
          if (!lbIsOpen || isZoomable(figures[next])) break;
        }
        show(next);
        if (thumbs[next] && typeof thumbs[next].scrollIntoView === 'function') {
          thumbs[next].scrollIntoView({ block: 'nearest', inline: 'nearest', behavior: 'smooth' });
        }
      }
      if (lbPrev) lbPrev.addEventListener('click', function(e) { e.preventDefault(); nav(-1); });
      if (lbNext) lbNext.addEventListener('click', function(e) { e.preventDefault(); nav(1); });
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', bootAll);
  } else {
    bootAll();
  }
})();
